import GeneralBody from "containers/EmployeesList/FormEmployees/GeneralBody";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMemo, useState } from "react";
import Button from "components/common/Button";
import { Button as ButtonAntd } from "antd";
import Constants from "helpers/constants";
import Colors from "assets/Colors";
import useUserMutations from "graphql/useUserMutations";
import bcrypt from "bcryptjs";
import { BSON } from "realm-web";
import { useTranslation } from "react-i18next";
import Schedule from "components/common/Schedule";
import FormHeaderWithTab from "components/common/FormHeaderWithTab";
import s from "./styles";

function AddWorker({
  closeModal,
  taxes,
  categories,
  subcategories,
  editItem = {},
  onUpdateSuccess,
  onAddSuccess,
  onDeleteRequest,
  setErrorMessage,
}) {
  const { t } = useTranslation();
  const { addUser, updateUser } = useUserMutations();
  const [section, setSection] = useState(Constants.GENERAL_SECTION);
  const [dbLoading, setDbLoading] = useState(false);
  const loading = dbLoading;

  let initialValues = {};

  if (editItem === null) {
    initialValues = {
      name: "",
      lastName: "",
      address1: "",
      address2: "",
      city: "",
      color: "",
      country: "",
      disabled: false,
      email: "",
      image: "",
      owner_id: "",
      password: "",
      phone: "",
      pin: "",
      ref: "",
      roles: "",
    };
  } else {
    initialValues = editItem;
  }

  const {
    handleSubmit,
    getFieldProps,
    values,
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
    isValid,
    dirty,
  } = useFormik({
    initialValues: {
      ...initialValues,
    },
    onSubmit: async (values) => {
      if (values.pin !== initialValues.pin) {
        const salt = bcrypt.genSaltSync(3);
        values.pin = bcrypt.hashSync(values.pin.toString(), salt);
      }

      const newValues = {
        _id: new BSON.ObjectId(),
        name: values.name,
        lastName: values.lastName,
        roles: values.roles,
        email: values.email,
        phone: values.phone,
        disabled: values.disabled,
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        country: values.country,
        pin: values.pin,
        color: values.color,
        updateDatetime: new Date(),
        creationDatetime: new Date(),
      };

      // add user
      try {
        setDbLoading(true);
        if (editItem === null) {
          await addUser(newValues);

          setDbLoading(false);
          onAddSuccess();
        } else {
          // eslint-disable-next-line no-unused-vars
          const { _id, ...uddateValues } = newValues;
          await updateUser(editItem, uddateValues);

          setDbLoading(false);
          onUpdateSuccess();
        }
        await closeModal();
      } catch (error) {
        setDbLoading(false);
        setErrorMessage({ error });
      }
    },

    validationSchema: Yup.object({
      name: Yup.string().required().max(255),
      roles: Yup.mixed().required(),
      pin: Yup.string().required(),
      email: Yup.string().email(),
    }),
  });

  function submit() {
    handleSubmit();
  }

  const itemDelete = {
    element: { ...editItem },
  };

  const randomNumber = () =>
    Math.floor(Math.random() * (9999 - 1000 + 1) + 1000);

  const saveButtonDisabled = !isValid || !dirty;

  const tabs = useMemo(() => {
    const generalTabs = [
      {
        id: Constants.GENERAL_SECTION,
        name: t("components.EmployeesList.AddWorker.Header.general"),
      },
      {
        id: Constants.EMPLOYEES_SCHEDULE,
        name: t("components.EmployeesList.AddWorker.Header.schedule"),
      },
    ];
    return generalTabs;
  }, []);

  return (
    <>
      <FormHeaderWithTab
        addTitle={t("components.EmployeesList.AddWorker.Header.addWorker")}
        updateTitle={t("components.EmployeesList.AddWorker.Header.editWorker")}
        tabs={tabs}
        section={section}
        setSection={setSection}
        closeModal={closeModal}
        onSave={() => submit()}
        enabled={isValid && dirty}
        saving={loading}
        updating={editItem}
      />
      <s.BodyContainer>
        {section === Constants.GENERAL_SECTION && (
          <GeneralBody
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            touched={touched}
            getFieldProps={getFieldProps}
            errors={errors}
            values={values}
            subcategories={subcategories}
            categories={categories}
            taxes={taxes}
            randomNumber={randomNumber}
          />
        )}
        {section === Constants.EMPLOYEES_SCHEDULE && (
          <>
            <div
              style={{
                padding: "0 12px",
                margin: "0 auto",
              }}
            >
              <s.SectionTitle>{t("Schedule.WorkingHours")}</s.SectionTitle>
              <s.Line style={{ marginTop: "18px" }} />
            </div>
            <Schedule
              setFieldValue={setFieldValue}
              touched={touched}
              getFieldProps={getFieldProps}
              errors={errors}
              values={values}
            />
          </>
        )}
        <s.BottomButtonsContainer>
          {editItem && editItem.roles[0] != "root" && (
            <ButtonAntd
              type="text"
              disabled={loading}
              style={{ color: Colors.notification_failed }}
              onClick={() => {
                onDeleteRequest(itemDelete);
              }}
            >
              {t("containers.EmployeesList.AddEmployees.deleteEmployees")}
            </ButtonAntd>
          )}

          <Button
            disabled={saveButtonDisabled}
            loading={loading}
            type="primary"
            style={{
              background: saveButtonDisabled
                ? `${Colors.primary}30`
                : Colors.primary,
              color: Colors.text_on_secondary,
              width: "169px",
              fontWeight: "500",
              border: "none",
              borderRadius: "5px",
              height: "40px",
            }}
            onClick={() => submit()}
          >
            {t("containers.EmployeesList.AddEmployees.save")}
          </Button>
        </s.BottomButtonsContainer>
      </s.BodyContainer>
    </>
  );
}

export default AddWorker;
